// SPDX-License-Identifier: GPL-3.0-or-later
// Tessa, the ten-x SigmaBot: 10X Orbit (THEME.md section 5.7).
// A gold bean bot with a headband. Her arm ring turns faster with each right orbit in the same ball, up to 10 steps.
// The ten step lamps always show the count, also with reduced motion.

import {
  BoxGeometry,
  CylinderGeometry,
  type Mesh,
  type MeshStandardMaterial,
  SphereGeometry,
  TorusGeometry,
} from "three";
import { clamp, numberArg } from "../animation.ts";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot, botFront } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  outlined,
  put,
  solid,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Tessa. */
export const TESSA_SPEC: SigmaBotSpec = {
  body: "bean",
  palette: "gold",
  eyes: "spark",
  mouth: "grin",
  hat: "headband",
  accessory: "none",
};

/** Size of one body unit of Tessa. */
const SCALE = 0.85;

/** Highest step. */
export const TESSA_MAX_STEP = 10;

/** Turn speed of the arm ring in rad/s for a step. */
export function tessaSpeed(step: number): number {
  return 0.3 + 0.3 * clamp(step, 0, TESSA_MAX_STEP);
}

export function buildTessa(env: CharacterEnv): Built {
  const group = namedGroup("Tessa");
  addBot(group, "TessaBot", TESSA_SPEC, SCALE);

  // Arm ring: ten arms at 36 degree intervals.
  const ring = namedGroup("TessaRing");
  ring.position.set(0, 45, 0);
  for (let i = 0; i < TESSA_MAX_STEP; i++) {
    const a = (i * 36 * Math.PI) / 180;
    ring.add(
      put(new BoxGeometry(3, 30, 3), solid(BRAND_HEX.edge), Math.cos(a) * 34, 0, Math.sin(a) * 34),
    );
  }
  group.add(ring);

  // The halo: a red fill disc over her head.
  group.add(outlined(put(new CylinderGeometry(18, 18, 2, 24), solid(BRAND_HEX.mark), 0, 82, 0)));
  const chest = makeLabel(env, 22, 12, 128, 72, (ctx, w, h) => drawTextPanel(ctx, w, h, ["10X"]));
  chest.mesh.position.set(0, 20, botFront(TESSA_SPEC, SCALE, 20) + 0.4);
  group.add(chest.mesh);

  // Step lamps: a column on the base. A lit lamp is a red fill with a white edge ring.
  const lamps: Mesh<SphereGeometry, MeshStandardMaterial>[] = [];
  const rims: Mesh<TorusGeometry, MeshStandardMaterial>[] = [];
  for (let i = 0; i < TESSA_MAX_STEP; i++) {
    const lamp = put(new SphereGeometry(3, 8, 8), solid(BRAND_HEX.gray333), -44, 6 + i * 7, 0);
    const rim = put(new TorusGeometry(4.2, 0.6, 6, 16), solid(BRAND_HEX.edge), -44, 6 + i * 7, 0);
    rim.name = `TessaRim${i}`;
    lamp.name = `TessaLamp${i}`;
    rim.visible = false;
    lamps.push(lamp);
    rims.push(rim);
    group.add(lamp, rim);
  }

  let step = 0;
  let angle = 0;
  let lastTime = Number.NaN;
  return {
    group,
    tick(timeMs) {
      const dt = Number.isNaN(lastTime) ? 0 : clamp((timeMs - lastTime) / 1000, 0, 0.1);
      lastTime = timeMs;
      angle += tessaSpeed(step) * dt * motion(env);
      ring.rotation.y = angle;
      lamps.forEach((lamp, i) => {
        const on = i < step;
        lamp.material.color.setHex(on ? BRAND_HEX.mark : BRAND_HEX.gray333);
        const rim = rims[i];
        if (rim) rim.visible = on;
      });
    },
    play(name, arg) {
      if (name === "step") step = Math.round(clamp(numberArg(arg, 0), 0, TESSA_MAX_STEP));
    },
    dispose() {
      disposeTree(group);
    },
  };
}
